import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle, Package } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const fromShopSlug = searchParams.get('from') || '';
  const continueTarget = fromShopSlug ? `/negozio/${fromShopSlug}` : '/';
  const trackingTarget = fromShopSlug ? `/tracking?from=${encodeURIComponent(fromShopSlug)}` : '/tracking';
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= 5) { setStatus('timeout'); return; }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
      setPaymentData(data);
      if (data.payment_status === 'paid') {
        setStatus('success');
      } else if (data.status === 'expired') {
        setStatus('expired');
      } else {
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div data-testid="payment-success-page" className="pb-safe px-4 pt-12 text-center max-w-md mx-auto">
      {status === 'checking' && (
        <div className="space-y-4">
          <Loader2 size={48} className="mx-auto text-[#C25934] animate-spin" />
          <h2 className="text-xl font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
            Verifica pagamento in corso...
          </h2>
          <p className="text-sm text-[#877E78]">Attendere qualche secondo</p>
        </div>
      )}

      {status === 'success' && (
        <div className="space-y-4">
          <div className="w-20 h-20 bg-[#4A6B53]/10 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle2 size={40} className="text-[#4A6B53]" />
          </div>
          <h2 className="text-2xl font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
            Pagamento Completato!
          </h2>
          <p className="text-[#5C554F]">
            Il tuo ordine è stato confermato. Riceverai una email di conferma.
          </p>
          {paymentData && (
            <div className="bg-[#F0EBE1] rounded-xl p-4 text-sm">
              <p className="text-[#5C554F]">Totale pagato: <strong className="text-[#C25934]">{(paymentData.amount / 100)?.toFixed(2)} &euro;</strong></p>
            </div>
          )}
          <div className="flex gap-3 justify-center pt-2">
            <Button onClick={() => navigate(trackingTarget)} className="bg-[#C25934] hover:bg-[#A64B2B] text-white rounded-full px-6 h-12">
              <Package size={16} className="mr-2" /> Traccia Ordine
            </Button>
            <Button onClick={() => navigate(continueTarget)} variant="outline" className="border-[#E8E3D9] rounded-full px-6 h-12">
              {fromShopSlug ? 'Torna al negozio' : 'Continua Shopping'}
            </Button>
          </div>
        </div>
      )}

      {(status === 'error' || status === 'expired' || status === 'timeout') && (
        <div className="space-y-4">
          <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto">
            <XCircle size={40} className="text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
            {status === 'timeout' ? 'Verifica in corso' : 'Pagamento non riuscito'}
          </h2>
          <p className="text-sm text-[#877E78]">
            {status === 'timeout'
              ? 'Il pagamento potrebbe essere ancora in elaborazione. Controlla la tua email.'
              : 'Si è verificato un errore. Riprova o contatta il negozio.'}
          </p>
          <Button onClick={() => navigate(fromShopSlug ? `/carrello?from=${encodeURIComponent(fromShopSlug)}` : '/carrello')} className="bg-[#C25934] hover:bg-[#A64B2B] text-white rounded-full px-6 h-12">
            Torna al Carrello
          </Button>
        </div>
      )}
    </div>
  );
}
